import { FC, useEffect, useState } from 'react';
import IQuiz from 'models/Quiz';
import TeamResultItem from './TeamResultItem';
import { useAppSelector } from 'hooks/useStore';
import { selectResult } from 'store/player/playerSelectors';
import { selectQuiz } from 'store/quiz/quizSelectors';
import { PAGE_SIZE, REFRESH_INTERVAL_MS } from './constants';
import classes from './styles.module.css';
import useCompactLayout from 'hooks/useCompactLayout';

type Props = {
  // When set, the page is driven externally (e.g. the compact leaderboard/QR slide
  // cycle in index.tsx) instead of rotating on its own internal timer.
  page?: number;
}

const TeamResultsList:FC<Props> = ({ page: controlledPage }) => {
  const isCompact = useCompactLayout();
  const isControlled = controlledPage !== undefined;

  const result:any = useAppSelector(selectResult);
  const storeQuiz = useAppSelector(selectQuiz);
  const quiz:IQuiz | null = storeQuiz || result?.quiz || null;
  const teamResults:any[] = result?.teamResults || [];

  const pageCount = Math.ceil(teamResults.length / PAGE_SIZE) || 1;
  const isPaginated = pageCount > 1;
  const [internalPage, setInternalPage] = useState<number>(0);

  useEffect(() => {
    if (isControlled || !isPaginated) return;
    const rotationInterval = setInterval(() => {
      setInternalPage((currentPage) => (currentPage + 1) % pageCount);
    }, REFRESH_INTERVAL_MS);
    return () => clearInterval(rotationInterval);
  }, [isControlled, isPaginated, pageCount]);

  useEffect(() => {
    if (isControlled) return;
    if (internalPage >= pageCount) setInternalPage(0);
  }, [isControlled, internalPage, pageCount]);

  if (!teamResults.length) return null;

  const page = isControlled ? Math.min(controlledPage as number, pageCount - 1) : internalPage;
  const pageStart = page * PAGE_SIZE;
  const pageResults = teamResults.slice(pageStart, pageStart + PAGE_SIZE);

  return (
    <div
      className={`relative overflow-hidden shrink-0 rounded-3xl w-full shadow-primary-border ${isCompact ? 'p-4' : 'mb-4 pt-6 sm:mb-8'}`}
      style={{ backgroundColor: quiz?.publicLeaderboard?.backgroundColor ?? result?.quizGroup?.publicLeaderboard?.backgroundColor ?? 'rgba(0,0,0,0.6)' }}
    >
      <div key={page} className={isPaginated ? classes.pageFade : ''}>
        {pageResults.map((teamResult, index) => (
          <TeamResultItem key={teamResult.team} resultIndex={pageStart + index} team={teamResult.team} totalScore={teamResult.totalScore} />
        ))}
      </div>
    </div>
  )
}

export default TeamResultsList;
